import { useEffect, useRef } from "react";

import { OrgChartNode } from "components/organisms/OrgTree";
import { ActionType } from "components/organisms/OrgTree/constants";
import { confirm } from "contexts";
import { useToggle } from "react-use";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import {
  useOrganizationChart,
  useOrganizationDetail,
  useDeptManagerDelete,
  useDeptDelete,
  useDeptEmployees,
} from "services";
import { useAuthStore } from "stores/Auth";
import { useOrgChartHook } from "stores/OrgChart";

export const useOrganizationalChart = () => {
  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);
  const [departmentDialog, toggleDepartmentDialog] = useToggle(false);
  const [editDepartment, setEditDepartment] = useToggle(false);
  const [employeeDialog, toggleEmployeeDialog] = useToggle(false);
  const {
    orgChart,
    setOrgChart,
    nodeSelected,
    setNodeSelected,
    addEmployees,
    deleteDeptManager,
    deleteDept,
    reset,
  } = useOrgChartHook();
  const { orgSelected } = useAuthStore();

  useOrganizationChart({
    options: {
      onSuccess: (res) => {
        setOrgChart(res?.data);
        setTimeout(() => {
          transformComponentRef.current?.centerView();
        }, 500);
      },
    },
  });
  const {
    data: orgData,
    isFetched,
    isLoading,
  } = useOrganizationDetail({
    params: orgSelected.id,
    options: { enabled: !!orgSelected.id },
  });
  const { mutate: getEmployeeService, isLoading: loadingGetListEmployee } =
    useDeptEmployees({
      options: {
        onSuccess: (res, context) => {
          addEmployees(
            Number(context?.departmentId ?? 0),
            res?.data?.items || []
          );
        },
      },
    });
  const { mutate: deleteDeptManagerService } = useDeptManagerDelete({
    options: {
      onSuccess: (_, context) => {
        deleteDeptManager(
          context?.departmentId || 0,
          Number(context.employeeId ?? 0),
          String(context.role ?? "")
        );
      },
    },
  });
  const { mutate: deleteDeptService } = useDeptDelete({
    options: {
      onSuccess: (_, context) => {
        deleteDept(context?.departmentId || 0);
      },
    },
  });

  const handleClickNode = (node: OrgChartNode, type: ActionType) => {
    setNodeSelected(node);

    switch (type) {
      case ActionType.ADD_DEPT:
        toggleDepartmentDialog();
        setEditDepartment(false);
        break;

      case ActionType.UPDATE_DEPT:
        toggleDepartmentDialog();
        setEditDepartment(true);
        break;

      case ActionType.ADD_EMPLOYEE:
        toggleEmployeeDialog();
        break;
      case ActionType.SHOW_EMPLOYEE:
        if (!node?.employees || node?.employees?.length === 0) {
          getEmployeeService({ departmentId: node?.id || 0 });
        }
        break;
      case ActionType.DELETE_DEPT_MANAGER:
        // eslint-disable-next-line no-case-declarations
        const userSelected = node?.deptManagers?.find(
          (item) =>
            item?.role === node?.extendInfo?.role &&
            item?.employee?.id === node?.extendInfo?.employeeId
        );
        confirm({
          title: "Delete Dept Manager",
          content: `Are you sure you want to delete ${
            userSelected?.employee?.fullName || ""
          } (${userSelected?.role || ""})?`,
        })
          .then(() =>
            deleteDeptManagerService({
              employeeId: Number(node?.extendInfo?.employeeId ?? 0),
              departmentId: node.id,
              role: String(node?.extendInfo?.role ?? ""),
            })
          )
          .catch(() => {});
        break;
      case ActionType.DELETE_DEPT:
        confirm({
          title: "Delete Dept",
          content: "Are you sure you want to delete this dept?",
        })
          .then(() =>
            deleteDeptService({
              departmentId: node.id,
            })
          )
          .catch(() => {});
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    return () => {
      reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    dialog: {
      department: {
        isEdit: editDepartment,
        show: departmentDialog,
        onToggle: toggleDepartmentDialog,
      },
      employee: {
        show: employeeDialog,
        onToggle: toggleEmployeeDialog,
      },
    },
    actions: {
      handleClickNode,
    },
    isFetched,
    nodeSelected,
    orgChart,
    orgName: orgData?.data?.name ?? "Talentnet",
    transformComponentRef,
    isLoading: isLoading || loadingGetListEmployee,
  };
};
